// The board portal channel (ADR 0029): myBRE (bre-loyalty's customer app)
// gives each current director the Board section by calling Hub server to
// server. Directors never sign in to Hub and hold no Hub capability: a seat
// is not access (plan §2.6). The channel mirrors the volunteer portal
// (ADR 0024) with its own secret, its own signature version, its own nonce
// and rate tables and its own switch on each side.
//
// Who is asking. myBRE has already signed the director in through BRE Auth;
// it asserts their BRE Auth subject on each request and signs the request
// with HUB_BOARD_SECRET (shared only by Hub's web Worker and myBRE's web
// Worker). Hub checks the signature, the time, that the nonce is new, and
// the rate per subject, then acts as that subject's Hub person, found by the
// exact (issuer, subject) pair. Core then checks, on every request, that the
// person holds an active director term today, and the office or committee
// role the route needs.
//
// Signature (version 1):
//
//   canonical = [
//     'bre-hub-board-v1',
//     METHOD,                    // upper case
//     pathWithQuery,             // as Hub receives it, e.g. /internal/board/v1/meetings
//     sub,                       // the BRE Auth subject
//     timestamp,                 // unix seconds, decimal
//     nonce,                     // 16-64 chars of [A-Za-z0-9_-], new for every request
//     sha256Hex(body),           // of the exact body bytes; of '' when there is none
//   ].join('\n')
//   signature = base64url(HMAC-SHA256(HUB_BOARD_SECRET, canonical))   // no padding
//
// Headers: x-hub-board-sub, x-hub-board-timestamp, x-hub-board-nonce,
// x-hub-board-signature. More than BOARD_SIGNATURE_MAX_SKEW_SECONDS from
// Hub's clock, or a nonce Hub has seen, is refused.

import type { CommitteeMe } from './governance-committees'
import { sha256Hex } from './volunteer-portal'

export const BOARD_PORTAL_PREFIX = '/internal/board/v1'
export const BOARD_SIGNATURE_VERSION = 'bre-hub-board-v1'
export const BOARD_SIGNATURE_MAX_SKEW_SECONDS = 60
export const BOARD_NONCE_TTL_SECONDS = 180
export const BOARD_RATE_LIMIT_PER_MINUTE = 90
export const BOARD_WRITE_LIMIT_PER_MINUTE = 20

export const BOARD_HEADERS = {
  sub: 'x-hub-board-sub',
  timestamp: 'x-hub-board-timestamp',
  nonce: 'x-hub-board-nonce',
  signature: 'x-hub-board-signature',
} as const

// The actor the web Worker signs for core on this channel: a subject with
// this prefix and no person id. Core lets such an actor reach only
// /v1/board-portal/* and resolves the person itself.
export const BOARD_PORTAL_SUB_PREFIX = 'board-portal:'
export const boardPortalSub = (breSub: string) => `${BOARD_PORTAL_SUB_PREFIX}${breSub}`
export const BOARD_PORTAL_CHANNEL = 'board-portal'
export const BOARD_ISSUER_HEADER = 'x-hub-board-issuer'

// Short-lived document links (a packet document opened from myBRE): signed
// by the web Worker with a key derived from HUB_BOARD_SECRET, good for this
// long, and checked again against the director's access when opened.
export const BOARD_DOCUMENT_LINK_SECONDS = 300
export const BOARD_DOCUMENT_LINK_VERSION = 'bre-hub-board-document-v1'

// The system actor that claims and reports governance emails (vote open,
// packet ready, send this email meeting, sign these minutes).
export const GOVERNANCE_EMAIL_SYSTEM_SUB = 'system:governance-email'

const NONCE = /^[A-Za-z0-9_-]{16,64}$/
const SUB = /^[A-Za-z0-9_.:@|-]{1,200}$/
const encoder = new TextEncoder()

function base64url(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '')
}

export function boardCanonical(input: { method: string; pathWithQuery: string; sub: string; timestamp: string; nonce: string; bodySha256: string }): string {
  return [BOARD_SIGNATURE_VERSION, input.method.toUpperCase(), input.pathWithQuery, input.sub, input.timestamp, input.nonce, input.bodySha256].join('\n')
}

async function hmac(secret: string, message: string): Promise<string> {
  if (!secret || secret.length < 32) throw new Error('HUB_BOARD_SECRET must be at least 32 characters')
  const key = await crypto.subtle.importKey('raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
  return base64url(new Uint8Array(await crypto.subtle.sign('HMAC', key, encoder.encode(message))))
}

// What myBRE does (its own copy lives in bre-loyalty lib/hub-board-client.ts;
// the test vector in board-portal.test.ts keeps the two honest).
export async function signBoardRequest(input: { secret: string; method: string; pathWithQuery: string; sub: string; body?: string; now?: number; nonce?: string }): Promise<Record<string, string>> {
  const timestamp = String(Math.floor((input.now ?? Date.now()) / 1000))
  const nonce = input.nonce ?? base64url(crypto.getRandomValues(new Uint8Array(18)))
  const signature = await hmac(input.secret, boardCanonical({ method: input.method, pathWithQuery: input.pathWithQuery, sub: input.sub, timestamp, nonce, bodySha256: await sha256Hex(input.body ?? '') }))
  return { [BOARD_HEADERS.sub]: input.sub, [BOARD_HEADERS.timestamp]: timestamp, [BOARD_HEADERS.nonce]: nonce, [BOARD_HEADERS.signature]: signature }
}

function equalStrings(a: string, b: string): boolean {
  if (a.length !== b.length) return false
  let diff = 0
  for (let i = 0; i < a.length; i += 1) diff |= a.charCodeAt(i) ^ b.charCodeAt(i)
  return diff === 0
}

export type BoardSignatureFailure = 'missing' | 'malformed' | 'stale' | 'bad_signature'

// Checks everything but the nonce's novelty (the caller keeps the nonces).
export async function verifyBoardRequest(input: {
  secret: string
  method: string
  pathWithQuery: string
  headers: { get(name: string): string | null }
  body: string
  now?: number
}): Promise<{ ok: true; sub: string; nonce: string; timestamp: number } | { ok: false; reason: BoardSignatureFailure }> {
  const sub = input.headers.get(BOARD_HEADERS.sub)
  const ts = input.headers.get(BOARD_HEADERS.timestamp)
  const nonce = input.headers.get(BOARD_HEADERS.nonce)
  const signature = input.headers.get(BOARD_HEADERS.signature)
  if (!sub || !ts || !nonce || !signature) return { ok: false, reason: 'missing' }
  if (!SUB.test(sub) || !/^\d{9,11}$/.test(ts) || !NONCE.test(nonce) || !/^[A-Za-z0-9_-]{43}$/.test(signature)) return { ok: false, reason: 'malformed' }
  const timestamp = Number(ts)
  const now = Math.floor((input.now ?? Date.now()) / 1000)
  if (Math.abs(now - timestamp) > BOARD_SIGNATURE_MAX_SKEW_SECONDS) return { ok: false, reason: 'stale' }
  const expected = await hmac(input.secret, boardCanonical({ method: input.method, pathWithQuery: input.pathWithQuery, sub, timestamp: ts, nonce, bodySha256: await sha256Hex(input.body) }))
  if (!equalStrings(expected, signature)) return { ok: false, reason: 'bad_signature' }
  return { ok: true, sub, nonce, timestamp }
}

// A document link token: base64url(JSON payload) + '.' + HMAC over it with
// a key derived from the channel secret (never the secret itself).
export type BoardDocumentToken = { d: string; v: number | null; s: string; e: number }

async function linkKey(secret: string) {
  return hmac(secret, BOARD_DOCUMENT_LINK_VERSION)
}

export async function signBoardDocumentToken(secret: string, payload: BoardDocumentToken): Promise<string> {
  const body = base64url(encoder.encode(JSON.stringify(payload)))
  return `${body}.${await hmac(await linkKey(secret), body)}`
}

export async function verifyBoardDocumentToken(secret: string, token: string, now = Date.now()): Promise<BoardDocumentToken | null> {
  const match = /^([A-Za-z0-9_-]{10,600})\.([A-Za-z0-9_-]{43})$/.exec(token)
  if (!match) return null
  const expected = await hmac(await linkKey(secret), match[1]!)
  if (!equalStrings(expected, match[2]!)) return null
  try {
    const json = atob(match[1]!.replaceAll('-', '+').replaceAll('_', '/'))
    const payload = JSON.parse(json) as BoardDocumentToken
    if (typeof payload.d !== 'string' || typeof payload.s !== 'string' || typeof payload.e !== 'number') return null
    if (payload.e * 1000 < now || payload.e * 1000 > now + (BOARD_DOCUMENT_LINK_SECONDS + 60) * 1000) return null
    return payload
  } catch {
    return null
  }
}

// ------------------------------------------------------------------ shapes
//
// What a director may see: their own meetings, packets, votes, policies,
// disclosure, action items, committees and term; the directory (names,
// offices, committees; contact details only where a director shares them);
// and the role extras their office gives them. Never another director's
// disclosure unless they are on the Executive Committee, never a ballot
// choice when a ballot is ordered (Phase 3), and executive-session content
// only for a meeting they were seated at.

export type BoardRoles = {
  offices: ('president' | 'vice_president' | 'past_president' | 'secretary' | 'treasurer')[]
  executiveCommittee: boolean
  committees: { id: string; name: string; kind: string; role: 'chair' | 'member' | 'ex_officio_non_voting'; voting: boolean }[]
  chairOf: string[]
  nominatingCommittee: boolean
  // Article IV, Section 4: the Secretary performs the President's duties
  // while both the President's and the Vice President's offices are vacant.
  performingDuties: ('president')[]
}

export type BoardMe = {
  isDirector: boolean
  notDirectorReason: string | null
  person: { id: string; name: string } | null
  roles: BoardRoles | null
  term: { seatLabel: string; seatKind: 'elected' | 'appointed'; seatClass: string | null; startsOn: string; lastDay: string } | null
  settings: { writtenConsentEnabled: boolean; emailMeetingSenders: string[] }
  today: string
  // The Committees section (ADR 0059): anyone on a committee, director or
  // not. A committee-only member has isDirector false and reaches only the
  // /committee/* routes.
  committee: CommitteeMe
  // ADR 0060: anyone nominated on the Nominating Committee's slate, director
  // or not, sees "My nomination" (/nominee).
  nominee?: { nominations: number; waiting: number }
}
